import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    User,
    Mail,
    Phone,
    Building2,
    Shield,
    X,
    Save,
    ChevronDown,
    ShieldCheck,
    ToggleRight,
} from "lucide-react";
import { GetRoles, CompanyDropdown } from "../../../Services/MasterDataService";
import { UpdateUserAPI } from "../../../Services/SurveyService";

const EditUser = ({ user, onClose, onUpdated }) => {
    const [roles, setRoles] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        mobile: "",
        companyId: "",
        role: "",
        status: "true",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await GetRoles();
                if (response?.data?.status === "success") {
                    setRoles(response?.data?.roleList || []);
                } else {
                    CustomToast("error", response?.data?.message || "Failed to fetch roles");
                }
            } catch (error) {
                CustomToast("error", "An error occurred while fetching roles");
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        const fetchCompanyList = async () => {
            try {
                const response = await CompanyDropdown();
                if (response?.data?.status === "success") {
                    setCompanyList(response?.data?.companyList || []);
                } else {
                    CustomToast("error", response?.data?.message || "Failed to fetch company list");
                }
            } catch (error) {
                CustomToast("error", "An error occurred while fetching company list");
            }
        };
        fetchCompanyList();
    }, []);

    // Prefill the form once user, roles, and companies are all available —
    // we match role/company by name since the user object carries names, not IDs.
    useEffect(() => {
        if (!user) return;

        const matchedRole = roles.find((r) => r.roleName === user.roleName);
        const matchedCompany = companyList.find((c) => c.companyName === user.companyName);

        setFormData((prev) => ({
            ...prev,
            fullName: user.fullName || "",
            email: user.email || "",
            mobile: user.mobileNumber || "",
            companyId: matchedCompany ? String(matchedCompany.companyID) : prev.companyId,
            role: matchedRole ? String(matchedRole.roleID) : prev.role,
            status: user.isActive === false ? "false" : "true",
        }));
    }, [user, roles, companyList]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        try {
            setSubmitting(true);
            const payload = {
                fullName: formData.fullName.trim(),
                hrUserUID: user?.hrUserUID,
                email: formData.email.trim(),
                mobileNumber: formData.mobile.trim(),
                companyID: Number(formData.companyId),
                roleID: Number(formData.role),
                gender: user?.gender || "",
                isActive: formData.status === "true",
            };
            const response = await UpdateUserAPI(payload);
            if (response?.data?.status === "success") {
                CustomToast("success", response?.data?.message || "User updated successfully.");
                onUpdated?.({
                    ...user,
                    fullName: payload.fullName,
                    email: payload.email,
                    mobileNumber: payload.mobileNumber,
                    companyName:
                        companyList.find((c) => String(c.companyID) === formData.companyId)?.companyName ||
                        user.companyName,
                    roleName:
                        roles.find((r) => String(r.roleID) === formData.role)?.roleName || user.roleName,
                    isActive: payload.isActive,
                });
                onClose();
                return;
            }
            CustomToast("fail", response?.data?.message || "Failed to update user.");
        } catch (error) {
            CustomToast("fail", error?.response?.data?.message || "An error occurred while updating the user.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            <motion.button
                type="button"
                aria-label="Close edit user drawer"
                onClick={onClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            />

            <motion.aside
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
                className="absolute right-0 top-0 h-full w-full max-w-4xl bg-[#f8f8fb] shadow-2xl"
            >
                <div className="flex h-full flex-col">
                    <div className="border-b border-gray-200 bg-white px-5 py-4 sm:px-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Edit User</h1>
                                <p className="mt-1 text-sm text-gray-500">Update this user's details.</p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                        <div className="flex items-start gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#c2a8f033]">
                                <ShieldCheck size={20} className="text-[#7f35b2]" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-[#7f35b2]">Manage users with ease</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Update user details, role, and account status.
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-md sm:p-6">
                            <div className="mx-auto max-w-2xl">
                                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                                    <div>
                                        <h2 className="inline-block border-b-2 border-[#7f35b2] pb-1 text-lg font-semibold text-[#7f35b2]">
                                            User Information
                                        </h2>
                                        <p className="mt-2 text-sm text-gray-500">Update the details of this user.</p>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="fullName"
                                                value={formData.fullName}
                                                onChange={handleChange}
                                                placeholder="Enter full name"
                                                required
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Enter email address"
                                                required
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Mobile Number <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                placeholder="Enter mobile number"
                                                required
                                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                            Company <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Building2 size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <select
                                                name="companyId"
                                                value={formData.companyId}
                                                onChange={handleChange}
                                                required
                                                className="w-full appearance-none rounded-lg border border-gray-200 py-2.5 pl-10 pr-8 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                            >
                                                <option value="">Select company</option>
                                                {companyList.map((company) => (
                                                    <option key={company.companyID} value={company.companyID}>
                                                        {company.companyName}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                                Role <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <Shield size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <select
                                                    name="role"
                                                    value={formData.role}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full appearance-none rounded-lg border border-gray-200 py-2.5 pl-10 pr-8 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                >
                                                    <option value="">Select role</option>
                                                    {roles.map((role) => (
                                                        <option key={role.roleID} value={role.roleID}>
                                                            {role.roleName}
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-gray-700">
                                                Status <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <ToggleRight size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <select
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full appearance-none rounded-lg border border-gray-200 py-2.5 pl-10 pr-8 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                                                >
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive</option>
                                                </select>
                                                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2 flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="flex items-center gap-2 rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                                        >
                                            <X size={16} />
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Save size={16} />
                                            {submitting ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.aside>
        </div>
    );
};

export default EditUser;